/**
 * ConfirmDialog — Provider component.
 *
 * Remplace les natifs `window.confirm/alert/prompt`. Voir
 * `contexts/confirm-dialog-context.ts` pour la doc d'utilisation.
 *
 * Adapte de scapino-portal/frontend/src/components/ConfirmDialog.tsx.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'

import {
  type AlertOptions,
  ConfirmContext,
  type ConfirmOptions,
  type DialogState,
  type PromptOptions,
} from '@/contexts/confirm-dialog-context'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'
import Modal from '@/components/ui/Modal'

import './ConfirmDialog.css'

export function ConfirmDialogProvider({ children }: { children: ReactNode }) {
  const [dialog, setDialog] = useState<DialogState | null>(null)
  const [promptValue, setPromptValue] = useState('')
  const confirmBtnRef = useRef<HTMLButtonElement>(null)
  const promptInputRef = useRef<HTMLInputElement>(null)

  const confirm = useCallback((opts: string | ConfirmOptions): Promise<boolean> => {
    const options: ConfirmOptions = typeof opts === 'string' ? { message: opts } : opts
    return new Promise<boolean>((resolve) => {
      setDialog({ type: 'confirm', options, resolve })
    })
  }, [])

  const alertFn = useCallback((opts: string | AlertOptions): Promise<void> => {
    const options: AlertOptions = typeof opts === 'string' ? { message: opts } : opts
    return new Promise<void>((resolve) => {
      setDialog({ type: 'alert', options, resolve })
    })
  }, [])

  const promptFn = useCallback((opts: string | PromptOptions): Promise<string | null> => {
    const options: PromptOptions = typeof opts === 'string' ? { message: opts } : opts
    setPromptValue(options.defaultValue ?? '')
    return new Promise<string | null>((resolve) => {
      setDialog({ type: 'prompt', options, resolve })
    })
  }, [])

  const handleConfirm = useCallback(() => {
    if (!dialog) return
    if (dialog.type === 'confirm') dialog.resolve(true)
    else if (dialog.type === 'alert') dialog.resolve()
    else dialog.resolve(promptValue.trim() || null)
    setDialog(null)
  }, [dialog, promptValue])

  const handleCancel = useCallback(() => {
    if (!dialog) return
    if (dialog.type === 'confirm') dialog.resolve(false)
    else if (dialog.type === 'alert') dialog.resolve()
    else dialog.resolve(null)
    setDialog(null)
  }, [dialog])

  useEffect(() => {
    if (!dialog) return
    if (dialog.type === 'prompt') {
      promptInputRef.current?.focus()
    } else {
      confirmBtnRef.current?.focus()
    }
  }, [dialog])

  const variant =
    dialog && dialog.type === 'confirm' ? (dialog.options.variant ?? 'default') : 'default'

  const title =
    dialog?.options.title ??
    (dialog?.type === 'alert'
      ? 'Information'
      : dialog?.type === 'prompt'
        ? 'Saisie'
        : 'Confirmation')

  const confirmText =
    dialog?.type === 'alert'
      ? 'OK'
      : dialog?.type === 'prompt'
        ? ((dialog.options as PromptOptions).confirmText ?? 'Valider')
        : ((dialog?.options as ConfirmOptions | undefined)?.confirmText ?? 'Confirmer')

  const cancelText =
    dialog?.type === 'alert'
      ? ''
      : dialog?.type === 'prompt'
        ? ((dialog.options as PromptOptions).cancelText ?? 'Annuler')
        : ((dialog?.options as ConfirmOptions | undefined)?.cancelText ?? 'Annuler')

  return (
    <ConfirmContext.Provider value={{ confirm, alert: alertFn, prompt: promptFn }}>
      {children}
      <Modal isOpen={!!dialog} onClose={handleCancel} title={title} size="small">
        {dialog && (
          <>
            <Modal.Body>
              <p className="confirm-dialog-message">{dialog.options.message}</p>
              {dialog.type === 'prompt' && (
                <Input
                  ref={promptInputRef}
                  type="text"
                  className="confirm-dialog-prompt-input"
                  value={promptValue}
                  onChange={(e) => setPromptValue(e.target.value)}
                  placeholder={(dialog.options as PromptOptions).placeholder ?? ''}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleConfirm()
                  }}
                />
              )}
            </Modal.Body>

            <Modal.Footer>
              {dialog.type !== 'alert' && (
                <Button variant="ghost" onClick={handleCancel}>
                  {cancelText}
                </Button>
              )}
              <Button
                ref={confirmBtnRef}
                variant={variant === 'danger' ? 'danger' : 'primary'}
                onClick={handleConfirm}
              >
                {confirmText}
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </ConfirmContext.Provider>
  )
}
